export type DebugSnapshot = () => unknown;

export class DebugRegistry {
  private snapshots = new Map<string, DebugSnapshot>();
  private staticEntries = new Map<string, unknown>();

  registerSnapshot(key: string, snapshot: DebugSnapshot): void {
    this.snapshots.set(key, snapshot);
  }

  registerStatic(key: string, value: unknown): void {
    this.staticEntries.set(key, value);
  }

  /** Install static entries as plain properties and snapshot entries as
   *  live getters on `target`. Returns `target` for chaining. */
  installOn<T extends Record<string, unknown>>(target: T): T {
    const writable = target as Record<string, unknown>;
    for (const [key, value] of this.staticEntries) writable[key] = value;
    for (const [key, fn] of this.snapshots) {
      Object.defineProperty(target, key, {
        configurable: true,
        enumerable: true,
        get: fn,
      });
    }
    return target;
  }

  /** Build a single object combining all static entries + all snapshot
   *  results. Snapshots are called fresh on each invocation so values
   *  reflect live state. */
  snapshot(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [key, value] of this.staticEntries) out[key] = value;
    for (const [key, fn] of this.snapshots) out[key] = fn();
    return out;
  }

  /** Snapshot keys, in registration order (static then dynamic). For
   *  test introspection. */
  keys(): string[] {
    return [...this.staticEntries.keys(), ...this.snapshots.keys()];
  }
}
